import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { latestAttr, humanizeToken, type AttrMap } from '../lib/inlineAttribution';
import { Badge, Button } from '../atoms';
import { StatCard, InlineEditCard, LastUpdated, CalcBreakdownPanel, EntityGroupsPanel } from '../organisms';
import { EntityHeader } from '../shell';
import { useEntityPermission } from '../lib/usePermissions';

/**
 * TaxRecordDetail — /tax-records/:id
 *
 * The JURISDICTION LINE record (§4). Replaces the generic schema-driven detail with
 * the layout the legacy Siger "Tax Record screen" defines, which is the reference the
 * team already works in: context links across the top, then the value basis, the tax
 * calculation method, the current rate and tax, and the estimates.
 *
 * This is where a cap lives. Yadi's 2026-08-04 ruling: a value cap exists at BOTH the
 * parcel level and the jurisdiction line, and the jurisdiction line is where it belongs
 * in the UI. So this page owns Tax Calculation Method · Cap · Starts · Ends, and the
 * parcel-level statutory clamp (§23.23, derived onto the parcel value) is surfaced
 * read-only beside it so the two are not confused. `capped_value` / `cap_year` are no
 * longer hand-editable on the values tab.
 *
 * MONEY-BLIND ROLES. Eleven of these fields are money columns
 * (redact-money.js → tax_records): tax_amount, taxable_value, rate, beginning_value,
 * current_value, exemption_amount, both reduction amounts, cap, and both estimates. The
 * API DELETES those keys for the money-blind `administrator`, so the panels that carry
 * them render a restricted note rather than a wall of em-dashes that would read as
 * "this parcel has no tax". Detection is by key ABSENCE, not by role and not by null —
 * a null means unset, an absent key means redacted, and inferring it from the payload
 * cannot drift from the backend's column list.
 */

interface TaxRecord {
  id: string;
  parcel_id: string;
  jurisdiction_id: string | null;
  account_number: string | null;
  tax_year: number;
  is_primary: boolean | number | null;
  // Value basis
  percent_of_assessed: number | null;
  tax_ratio: number | null;
  overrides: string | null;
  use_alternate_value: boolean | number | null;
  beginning_value?: number | null;
  current_value?: number | null;
  taxable_value?: number | null;
  exemption_amount?: number | null;
  // Reduction, mirrored from the parcel value (both ends)
  reduction_beginning_type: string | null;
  reduction_beginning_status: string | null;
  reduction_beginning_amount?: number | null;
  reduction_current_type: string | null;
  reduction_current_status: string | null;
  reduction_current_amount?: number | null;
  // Calculation method
  tax_calc_method: string;
  cap?: number | null;
  start_year: number | null;
  end_year: number | null;
  // Rate and tax
  rate?: number | null;
  rate_is_estimate: boolean | number | null;
  use_jurisdictions_rate: boolean | number | null;
  tax_amount?: number | null;
  do_not_calculate_taxes: boolean | number | null;
  estimate_rate?: number | null;
  estimate_tax?: number | null;
  updated_by?: string | null;
  updated_at?: string | null;
}

interface NamedRow { id: string; name: string | null }
interface ParcelRow { id: string; account_number: string; name: string | null; property_id: string; assessor_id: string | null }
interface PropertyRow { id: string; name: string | null; client_id: string | null; location_code: string | null }
/** The parcel's current value row — carries the §23.23 parcel-level clamp. */
interface ValueRow { id: string; tax_year: number; is_current_value: boolean | number | null; capped_value: number | null; cap_year: number | null }

const isOn = (v: unknown): boolean => v === true || v === 1 || v === '1';

function money(v: unknown): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isNaN(n) ? '—' : `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}
/** A rate prints at full stored precision — 0.258162 is meaningful to the cent. */
function rateOf(v: unknown): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isNaN(n) ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 6 });
}
function ratio(v: unknown): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isNaN(n) ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
}
function yearOf(v: unknown): string {
  return v == null || v === '' ? '—' : String(v);
}

/** The restricted stand-in for a panel whose figures the caller may not see. */
function RestrictedPanel({ title, note }: { title: string; note: string }) {
  return (
    <div style={{
      background: 'var(--color-card)', border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)',
    }}>
      <h3 style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)' }}>{title}</h3>
      <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>{note}</p>
    </div>
  );
}

/**
 * An entity link inside a read-only row. `reset.css` sets `a { color: inherit }`, so an
 * unstyled Link is indistinguishable from plain text — the context rows are the page's
 * only navigation, so they have to read as interactive. Matches the ClientDetail
 * parent-client link.
 */
function EntityLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} style={{ color: 'var(--brand-primary)', fontWeight: 'var(--fw-medium)' }}>{children}</Link>
  );
}

/** One label/value row in a read-only card. */
function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)', padding: '2px 0', fontSize: 'var(--fs-sm)' }}>
      <span style={{ color: 'var(--color-ink-3)' }}>{label}</span>
      <span style={{ color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)', textAlign: 'right' }}>{value}</span>
    </div>
  );
}

function ReadOnlyCard({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--color-card)', border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)',
    }}>
      <h3 style={{ margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)' }}>{title}</h3>
      {hint && <p style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{hint}</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{children}</div>
    </div>
  );
}

export function TaxRecordDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const recordQuery = useQuery<TaxRecord>({
    queryKey: ['tax-record', id],
    queryFn: ({ signal }) => api.get<TaxRecord>(`/api/tax-records/${id}`, signal),
    enabled: !!id,
  });
  const rec = recordQuery.data;

  // Context chain for the header links, each an independent best-effort read: a
  // missing link renders as plain text rather than failing the page.
  const parcelQuery = useQuery<ParcelRow>({
    queryKey: ['parcel', rec?.parcel_id],
    queryFn: ({ signal }) => api.get<ParcelRow>(`/api/parcels/${rec!.parcel_id}`, signal),
    enabled: !!rec?.parcel_id,
  });
  const parcel = parcelQuery.data;
  const propertyQuery = useQuery<PropertyRow>({
    queryKey: ['property', parcel?.property_id],
    queryFn: ({ signal }) => api.get<PropertyRow>(`/api/properties/${parcel!.property_id}`, signal),
    enabled: !!parcel?.property_id,
  });
  const property = propertyQuery.data;
  const clientQuery = useQuery<NamedRow>({
    queryKey: ['client', property?.client_id],
    queryFn: ({ signal }) => api.get<NamedRow>(`/api/clients/${property!.client_id}`, signal),
    enabled: !!property?.client_id,
  });
  const assessorQuery = useQuery<NamedRow>({
    queryKey: ['assessor', parcel?.assessor_id],
    queryFn: ({ signal }) => api.get<NamedRow>(`/api/assessors/${parcel!.assessor_id}`, signal),
    enabled: !!parcel?.assessor_id,
  });
  const jurisdictionQuery = useQuery<NamedRow>({
    queryKey: ['jurisdiction', rec?.jurisdiction_id],
    queryFn: ({ signal }) => api.get<NamedRow>(`/api/jurisdictions/${rec!.jurisdiction_id}`, signal),
    enabled: !!rec?.jurisdiction_id,
  });

  // The parcel's CURRENT value row for this year — the §23.23 clamp is derived onto it.
  const valuesQuery = useQuery<ValueRow[]>({
    queryKey: ['parcel-values', rec?.parcel_id],
    queryFn: ({ signal }) => api.get<ValueRow[]>(`/api/parcel-values?parcel_id=${rec!.parcel_id}`, signal),
    enabled: !!rec?.parcel_id,
  });
  const attrQuery = useQuery<AttrMap>({
    queryKey: ['field-attribution', 'tax_records', id],
    queryFn: ({ signal }) => api.get<AttrMap>(`/api/field-attribution/tax_records/${id}`, signal),
    enabled: !!id,
  });

  const canEdit = useEntityPermission('tax_record', 'can_edit');

  if (recordQuery.isLoading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading tax record…</p>;
  if (recordQuery.error) {
    const notFound = recordQuery.error instanceof ApiClientError && recordQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Tax record not found' : 'Failed to load tax record'}</h2>
        <p style={{ margin: 0 }}>{(recordQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>← Back</Button>
        </p>
      </div>
    );
  }
  if (!rec) return null;

  // Money-blind detection: the API deletes money keys, so ABSENCE (not null) is the
  // signal. `tax_amount` stands for the set — redactMoney strips the whole list at once.
  const moneyHidden = !('tax_amount' in rec);
  const restrictedNote = 'Dollar figures are hidden for your role. The calculation method, its window, and the value basis are shown.';

  const record = rec as unknown as Record<string, unknown>;
  const attr: AttrMap = attrQuery.data ?? {};
  const invalidate: unknown[][] = [
    ['tax-record', rec.id],
    ['field-attribution', 'tax_records', rec.id],
    ['tax-records', rec.parcel_id],
  ];

  const jurisdictionName = jurisdictionQuery.data?.name ?? null;
  const currentValue = (valuesQuery.data ?? []).find((v) => v.tax_year === rec.tax_year && isOn(v.is_current_value)) ?? null;

  // Siger: "Use Jurisdiction's — when this field is checked, the Rate field cannot be
  // manually edited." Mirrored literally, so the two controls cannot disagree.
  const rateLocked = isOn(rec.use_jurisdictions_rate);
  // Siger: "Overrides — when an Override is selected, the Taxable Value can be changed
  // at will." So the override IS the permission to hand-key taxable, and the audit trail
  // for why taxable != assessed x ratio.
  const taxableUnlocked = !!(rec.overrides && String(rec.overrides).trim());

  const methodLabel = humanizeToken(rec.tax_calc_method || 'standard');
  const capWindow = rec.start_year || rec.end_year
    ? `${yearOf(rec.start_year)} – ${yearOf(rec.end_year)}`
    : null;
  // An out-of-window cap is INERT: resolveCapMethod requires the line's tax_year to sit
  // inside [start_year, end_year]. Say so, rather than showing a live-looking cap.
  const capExpired = rec.end_year != null && Number(rec.tax_year) > Number(rec.end_year);
  const capNotYet = rec.start_year != null && Number(rec.tax_year) < Number(rec.start_year);

  return (
    <>
      <EntityHeader
        prefix={`Tax record · ${rec.tax_year}`}
        title={jurisdictionName ?? rec.account_number ?? 'Tax record'}
        subtitle={parcel ? `${parcel.account_number}${parcel.name ? ` · ${parcel.name}` : ''}` : undefined}
        statusPills={[
          <Badge key="method" variant={rec.tax_calc_method === 'standard' ? 'neutral' : 'info'}>{methodLabel}</Badge>,
          ...(isOn(rec.is_primary) ? [<Badge key="primary" variant="success">Primary</Badge>] : []),
          ...(isOn(rec.do_not_calculate_taxes) ? [<Badge key="dnc" variant="warning">Not calculated</Badge>] : []),
          ...(capExpired ? [<Badge key="exp" variant="neutral">Cap expired</Badge>] : []),
        ]}
        actions={[<Button key="back" variant="secondary" size="sm" onClick={() => navigate(-1)}>← Back</Button>]}
        kpis={[
          <StatCard key="taxable" label="Taxable value" value={moneyHidden ? '—' : money(rec.taxable_value)} caption={moneyHidden ? 'hidden for your role' : 'after exemptions'} />,
          <StatCard key="tax" label="Tax" value={moneyHidden ? '—' : money(rec.tax_amount)} caption={isOn(rec.do_not_calculate_taxes) ? 'not recalculated' : 'at the current rate'} />,
          <StatCard key="rate" label="Rate" value={moneyHidden ? '—' : rateOf(rec.rate)} caption={rateLocked ? "jurisdiction's rate" : isOn(rec.rate_is_estimate) ? 'estimate' : 'record rate'} />,
          <StatCard key="method" label="Method" value={methodLabel} caption={capWindow ?? 'no window set'} />,
        ]}
      />

      {/* Context — the links Siger puts across the top of the screen. */}
      <ReadOnlyCard title="Context" hint="The records this tax line is derived from.">
        <Row label="Client" value={clientQuery.data?.name
          ? <EntityLink to={`/clients/${property!.client_id}`}>{clientQuery.data.name}</EntityLink> : '—'} />
        <Row label="Property" value={property?.name
          ? <EntityLink to={`/properties/${property.id}`}>{property.name}</EntityLink> : '—'} />
        <Row label="Location code" value={property?.location_code || '—'} />
        <Row label="Assessor" value={assessorQuery.data?.name
          ? <EntityLink to={`/assessors/${parcel!.assessor_id}`}>{assessorQuery.data.name}</EntityLink> : '—'} />
        <Row label="Parcel" value={parcel
          ? <EntityLink to={`/parcels/${parcel.id}`}>{parcel.account_number}</EntityLink> : '—'} />
        <Row label="Jurisdiction" value={jurisdictionName
          ? <EntityLink to={`/jurisdictions/${rec.jurisdiction_id}`}>{jurisdictionName}</EntityLink> : '—'} />
      </ReadOnlyCard>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 22rem), 1fr))', gap: 'var(--sp-4)', marginTop: 'var(--sp-4)' }}>
        <InlineEditCard
          title="Identification"
          routePath="tax-records"
          recordId={rec.id}
          record={record}
          canEdit={canEdit}
          invalidateKeys={invalidate}
          attribution={latestAttr(attr, ['account_number', 'is_primary'])}
          fields={[
            { key: 'account_number', label: "Jurisdiction's account number", type: 'text' },
            { key: 'is_primary', label: 'Primary line', type: 'checkbox' },
          ]}
        />

        {/* Value basis. `taxable_value` is editable only when an Override explains why it
            departs from assessed x ratio — Siger's rule, kept literal. */}
        {moneyHidden ? (
          <RestrictedPanel title="Value basis" note={restrictedNote} />
        ) : (
          <InlineEditCard
            title="Value basis"
            routePath="tax-records"
            recordId={rec.id}
            record={record}
            canEdit={canEdit}
            invalidateKeys={invalidate}
            attribution={latestAttr(attr, ['percent_of_assessed', 'tax_ratio', 'overrides', 'use_alternate_value', 'beginning_value', 'current_value', 'taxable_value'])}
            fields={[
              { key: 'percent_of_assessed', label: "Percent of ass'd", type: 'number', display: ratio },
              { key: 'tax_ratio', label: 'Tax ratio', type: 'number', display: ratio },
              {
                key: 'overrides', label: 'Overrides', type: 'text',
                hint: 'Why taxable departs from assessed × ratio. Setting one unlocks the taxable value.',
              },
              { key: 'use_alternate_value', label: 'Alternate value', type: 'checkbox' },
              { key: 'beginning_value', label: 'Beginning value', type: 'number', display: money },
              { key: 'current_value', label: 'Current value', type: 'number', display: money },
              {
                key: 'taxable_value', label: 'Taxable value', type: 'number', display: money,
                readOnly: !taxableUnlocked,
                hint: taxableUnlocked
                  ? 'Editable because an override is set.'
                  : 'Derived from the value and ratio. Set an override to hand-key it.',
              },
              { key: 'exemption_amount', label: 'Exemption applied', type: 'number', display: money, readOnly: true },
            ]}
          />
        )}

        {/* Tax calculation method — §4's home for the cap. Never money-gated as a whole:
            the METHOD and its window are not money, only `cap` is, so a money-blind
            operator can still see and set which method applies.

            The window state is rendered as VISIBLE text below the card, not as a field
            hint: InlineEditCard only shows hints in edit mode, and "this cap no longer
            applies" is precisely what someone READING the record needs. It is also the one
            place we knowingly diverge from Siger, which rewrites the stored method on roll
            while we leave it and gate at calculation time — so the page has to say so. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <InlineEditCard
            title="Tax calculation method"
            routePath="tax-records"
            recordId={rec.id}
            record={record}
            canEdit={canEdit}
            invalidateKeys={invalidate}
            attribution={latestAttr(attr, ['tax_calc_method', 'cap', 'start_year', 'end_year'])}
            fields={[
              {
                key: 'tax_calc_method', label: 'Method', type: 'select',
                options: ['standard', 'capped_tax', 'capped_rate', 'capped_value', 'fixed_rate', 'fixed_tax', 'fixed_value']
                  .map((v) => ({ value: v, label: humanizeToken(v) })),
                hint: rec.tax_calc_method === 'capped_value'
                  ? 'Capped value clamps the appraised figure before exemptions, upstream of the ratio.'
                  : undefined,
              },
              ...(moneyHidden ? [] : [{
                key: 'cap', label: 'Cap', type: 'number' as const, display: money,
                hint: 'An amount or a rate, depending on the method.',
              }]),
              { key: 'start_year', label: 'Starts', type: 'number' },
              { key: 'end_year', label: 'Ends', type: 'number' },
            ]}
          />
          {(capExpired || capNotYet) && rec.tax_calc_method !== 'standard' && (
            <p style={{
              margin: 0, padding: 'var(--sp-2) var(--sp-3)', borderRadius: 'var(--r-sm)',
              background: 'var(--color-bg)', border: '1px solid var(--color-border)',
              fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)',
            }}>
              {capExpired
                ? `Ended ${rec.end_year} — this cap no longer applies to the ${rec.tax_year} line, which is taxed as standard.`
                : `Starts ${rec.start_year} — not yet applied to the ${rec.tax_year} line, which is taxed as standard.`}
            </p>
          )}
        </div>

        {/* Current rate and tax. Rate is read-only while the line follows the
            jurisdiction's rate, exactly as Siger describes. */}
        {moneyHidden ? (
          <RestrictedPanel title="Current rate and tax" note={restrictedNote} />
        ) : (
          <InlineEditCard
            title="Current rate and tax"
            routePath="tax-records"
            recordId={rec.id}
            record={record}
            canEdit={canEdit}
            invalidateKeys={invalidate}
            attribution={latestAttr(attr, ['rate', 'rate_is_estimate', 'use_jurisdictions_rate', 'tax_amount', 'do_not_calculate_taxes'])}
            fields={[
              {
                key: 'rate', label: 'Rate', type: 'number', display: rateOf,
                readOnly: rateLocked,
                hint: rateLocked ? "Following the jurisdiction's rate. Untick below to set it here." : undefined,
              },
              { key: 'rate_is_estimate', label: 'Rate is an estimate', type: 'checkbox' },
              { key: 'use_jurisdictions_rate', label: "Use jurisdiction's rate", type: 'checkbox' },
              { key: 'tax_amount', label: 'Tax', type: 'number', display: money },
              {
                key: 'do_not_calculate_taxes', label: 'Do not calculate taxes', type: 'checkbox',
                hint: 'Stops the tax being recalculated when a rate or value changes.',
              },
            ]}
          />
        )}

        {/* Exemption — mirrored from the parcel value, both ends. Read-only here: the
            parcel value is where an exemption is authored.

            The `reduction_*` COLUMNS keep their names (renaming them is a migration,
            and the tax-bill surfaces are part 3's) — but the word a person reads is
            "Exemption" per part 2 §1.1, because this card mirrors the exemption
            record, not the billing basis. */}
        <ReadOnlyCard title="Exemption" hint="Set at the parcel value. Shown here for both ends of the appeal.">
          <Row label="Beginning type" value={rec.reduction_beginning_type ? humanizeToken(rec.reduction_beginning_type) : '—'} />
          <Row label="Beginning status" value={rec.reduction_beginning_status ? humanizeToken(rec.reduction_beginning_status) : '—'} />
          <Row label="Beginning amount" value={moneyHidden ? 'hidden for your role' : money(rec.reduction_beginning_amount)} />
          <Row label="Current type" value={rec.reduction_current_type ? humanizeToken(rec.reduction_current_type) : '—'} />
          <Row label="Current status" value={rec.reduction_current_status ? humanizeToken(rec.reduction_current_status) : '—'} />
          <Row label="Current amount" value={moneyHidden ? 'hidden for your role' : money(rec.reduction_current_amount)} />
        </ReadOnlyCard>

        {/* Estimates — what the engine computes, alongside the stored figures. */}
        <ReadOnlyCard title="Estimates" hint="What the engine currently computes for this line.">
          <Row label="Estimate rate" value={moneyHidden ? 'hidden for your role' : rateOf(rec.estimate_rate)} />
          <Row label="Estimate tax" value={moneyHidden ? 'hidden for your role' : money(rec.estimate_tax)} />
        </ReadOnlyCard>

        {/* The OTHER cap — the parcel-level statutory clamp. Yadi: a value cap exists at
            both levels. This one is derived by the §23.23 rule onto the parcel value and
            read upstream of the ratio, so it is deliberately read-only here and labelled
            to distinguish it from the line's own cap method above. */}
        <ReadOnlyCard
          title="Parcel-level value cap"
          hint="Derived on the parcel value and applied before the ratio. Distinct from the line's cap method above."
        >
          <Row label="Capped value" value={currentValue == null ? '—' : money(currentValue.capped_value)} />
          <Row label="Cap year" value={currentValue == null ? '—' : yearOf(currentValue.cap_year)} />
          {currentValue && currentValue.capped_value != null && (
            <Row label="Source" value={<EntityLink to={`/parcel-values/${currentValue.id}`}>Parcel value {currentValue.tax_year}</EntityLink>} />
          )}
        </ReadOnlyCard>
      </div>

      {/* The derivation, on the page that shows the derived numbers.
          This panel existed and was UNREACHABLE: it was rendered only by
          GenericDetail behind `config.table === 'tax_records'`, and no route
          mounts GenericDetail for tax records -- /tax-records/:id has always
          come here. So the one screen built to explain a taxable value could
          not be opened, and the fields above stated results with no way to ask
          where they came from. */}
      {id && (
        <section style={{ marginTop: 'var(--sp-6)' }}>
          <CalcBreakdownPanel taxRecordId={id} />
        </section>
      )}

      <div style={{ marginTop: 'var(--sp-4)' }}>
        <LastUpdated updatedBy={rec.updated_by ?? null} updatedAt={rec.updated_at ?? null} />
      </div>

      <div style={{ marginTop: 'var(--sp-5)' }}>
        <EntityGroupsPanel entityType="tax_record" entityId={id} />
      </div>
    </>
  );
}
